<template>
    <div>
        <div class="container gy-5">
            <div class="row align-items-center justify-content-start my-4">
                <div class="flex-grow-0 col" v-if="canAddFacility && !waitingForReview && !evaluatedOnly">
                    <el-button type="primary" icon="Plus" @click="openAddNFDialog">Add National Facility</el-button>
                </div>
                <div class="flex-grow-1 col">
                    <el-input v-model="settings.query" placeholder="acronym or institute" @input="queryChanged">
                        <template #prepend>Search by</template>
                    </el-input>
                </div>
                <div class="col flex-grow-1 col">
                    <el-pagination
                        v-model:current-page="currentPage"
                        v-model:page-size="settings.perPage"
                        :total="totalCount"
                        :page-sizes="settings.perPageOptions"
                        layout="prev, pager, next, sizes, total"
                        @size-change="pageSizeChanged"
                        @update:current-page="refreshTable"
                        background
                        style="justify-content: end"
                    />
                </div>
            </div>
        </div>
        <el-table stripe :data="displayedFacilities" style="width: 100%" table-layout="auto" v-loading="facilitiesLoading">
            <el-table-column label="Facility" class-name="facility-cell">
                <template #default="scope">
                    <div class="d-flex gap-2 mt-2">
                        <el-tag v-if="scope.row.type?.code === 'actris'" type="primary" class="flex-grow-1" style="flex-basis: 0">{{ scope.row.type.name }}</el-tag>
                        <el-tag v-else-if="scope.row.type?.code === 'actris-MP'" type="primary" class="flex-grow-1" style="flex-basis: 0">{{ scope.row.type.name }}</el-tag>
                        <el-tag v-else-if="scope.row.type?.code === 'earlinet'" type="info" class="flex-grow-1" style="flex-basis: 0">{{ scope.row.type.name }}</el-tag>
                        <el-tag v-else-if="scope.row.type?.code === 'other'" type="warning" class="flex-grow-1" style="flex-basis: 0">Other type</el-tag>
                        <el-tag v-else type="warning" class="flex-grow-1" style="flex-basis: 0">{{ scope.row?.type?.name || 'Unknown type' }}</el-tag>
                    </div>
                    <div class="d-flex gap-1 pt-3 align-items-end">
                        <el-badge :type="initialAcceptanceColor(scope.row)" :value="scope.row?.initial_acceptance?.status?.name" badge-class="facility-status-badge">
                            <el-text style="font-size: var(--el-font-size-extra-large); line-height: var(--el-font-size-extra-large); display: inline-flex">
                                {{scope.row.acronym}}
                            </el-text>
                        </el-badge>
                        <template v-if="showAdminActions">
                            <el-text v-if="scope.row.displayed_status?.code === 'certified'" type="success" class="flex-grow-1 text-start" style="flex-basis: 0">Certified</el-text>
                            <el-text v-else-if="scope.row.displayed_status?.code === 'not-active'" type="danger" class="flex-grow-1 text-start" style="flex-basis: 0">Not active</el-text>
                            <el-text v-else-if="scope.row.displayed_status?.code === 'under-review'" type="warning" class="flex-grow-1 text-start" style="flex-basis: 0">Under review</el-text>
                            <el-text v-else-if="scope.row.displayed_status?.code === 'technically-compliant'" type="primary" class="flex-grow-1 text-start" style="flex-basis: 0">Technically compliant</el-text>
                            <el-text v-else type="info" class="flex-grow-1 text-start" style="flex-basis: 0">{{ scope.row?.displayed_status?.name || 'Unknown status' }}</el-text>
                        </template>
                    </div>
                    <span style="font-size: var(--el-font-size-small); line-height: var(--el-font-size-extra-large); display: inline-flex" class="align-bottom" v-if="scope.row.hosting_institute">{{scope.row.hosting_institute}}</span>
                </template>
            </el-table-column>
            <el-table-column label="Contact" v-if="showContact">
                <template #default="scope">
                    <template v-if="scope.row.pi_contact">
                        <el-link type="default" v-if="scope.row.pi_contact" :href="`mailto:${scope.row.pi_contact}`">{{ scope.row.pi_name || "N/A" }}</el-link>
                        <span> (ARS)</span>
                        <br />
                    </template>
                    <template v-if="scope.row.lidar_pi_contact">
                        <el-link type="default" v-if="scope.row.lidar_pi_contact" :href="`mailto:${scope.row.lidar_pi_contact}`">{{ scope.row.lidar_pi_name || "N/A" }}</el-link>
                        <span> (Lidar)</span>
                        <br />
                    </template>
                    <template v-if="scope.row.photometer_pi_contact">
                        <el-link type="default" v-if="scope.row.photometer_pi_contact" :href="`mailto:${scope.row.photometer_pi_contact}`">{{ scope.row.photometer_pi_name || "N/A" }}</el-link>
                        <span> (Photometer)</span>
                        <br />
                    </template>
                    <template v-if="scope.row.ceilometer_pi_contact">
                        <el-link type="default" v-if="scope.row.ceilometer_pi_contact" :href="`mailto:${scope.row.ceilometer_pi_contact}`">{{ scope.row.ceilometer_pi_name || "N/A" }}</el-link>
                        <span> (Ceilometer)</span>
                        <br />
                    </template>
                </template>
            </el-table-column>
            <el-table-column label="Reviewers" v-if="showReviewers" width="300">
                <template #default="scope">
                    <template v-if="scope.row.initial_acceptance?.reviewers?.lidar">
                        <el-link type="default" :href="`mailto:${scope.row.initial_acceptance?.reviewers?.lidar?.email}`">{{ scope.row.initial_acceptance?.reviewers?.lidar?.first_name }} {{ scope.row.initial_acceptance?.reviewers?.lidar?.last_name }}</el-link>
                        (Lidar)
                        <br />
                    </template>
                    <template v-if="scope.row.initial_acceptance?.reviewers?.lidar_data">
                        <el-link type="default" :href="`mailto:${scope.row.initial_acceptance?.reviewers?.lidar_data?.email}`">{{ scope.row.initial_acceptance?.reviewers?.lidar_data?.first_name }} {{ scope.row.initial_acceptance?.reviewers?.lidar_data?.last_name }}</el-link>
                        (Lidar Data)
                        <br />
                    </template>
                    <template v-if="scope.row.initial_acceptance?.reviewers?.photometer">
                        <el-link type="default" :href="`mailto:${scope.row.initial_acceptance?.reviewers?.photometer?.email}`">{{ scope.row.initial_acceptance?.reviewers?.photometer?.first_name }} {{ scope.row.initial_acceptance?.reviewers?.photometer?.last_name }}</el-link>
                        (Photometer)
                        <br />
                    </template>
                    <template v-if="scope.row.initial_acceptance?.reviewers?.photometer_data">
                        <el-link type="default" :href="`mailto:${scope.row.initial_acceptance?.reviewers?.photometer_data?.email}`">{{ scope.row.initial_acceptance?.reviewers?.photometer_data?.first_name }} {{ scope.row.initial_acceptance?.reviewers?.photometer_data?.last_name }}</el-link>
                        (Photometer Data)
                        <br />
                    </template>
                </template>
            </el-table-column>
            <el-table-column label="Actions" width="300">
                <template #default="scope">
                    <div class="d-flex flex-wrap gap-2">
                        <el-tooltip v-if="canSendInvite(scope.row)" :content="scope.row?.initial_acceptance?.status?.code === 'invited' ? 'Resend invitation' : 'Send invitation email'" placement="top" :enterable="false">
                            <el-button class="ms-0" type="primary" plain icon="Message" size="small" circle @click="confirmInvite(scope.row.id)" />
                        </el-tooltip>
                        <EditFacilityForm v-if="showAdminActions && isAdmin" v-model="scope.row" @saved="reloadSavedFacility($event)" @cancel="reloadSavedFacility($event)">
                            <template #default="{ open }">
                                <el-button @click="open" plain round type="primary" size="small" class="flex-grow-1" icon="EditPen">Edit facility</el-button>
                            </template>
                        </EditFacilityForm>
                        <router-link :to="{name: 'FacilityProfile', params: { facilityId: scope.row.id }}" custom v-slot="{navigate}" v-if="showProfile">
                            <el-button type="primary" plain round size="small" @click="navigate" icon="Right" class="flex-grow-1 ms-0">Profile</el-button>
                        </router-link>
                        <template v-if="showAcceptance">
                            <el-button-group class="d-flex flex-grow-1" style="flex-basis: 100%;">
                                <el-button @click="$router.push({name: 'FacilityIa', params: { facilityId: scope.row.id }})" type="primary" class="flex-grow-1" plain round size="small" icon="Right" style="border-right: 1px solid var(--el-color-primary-light-5) !important; margin-right: -1px;">Acceptance</el-button>
                                <el-dropdown  trigger="click" class="align-start flex-grow-1">
                                    <el-button type="primary" plain round size="small" class="flex-grow-1" style="border: 1px solid var(--el-color-primary-light-5) !important; margin-right: 0">
                                        Documents<el-icon class="el-icon--right"><ArrowDown /></el-icon>
                                    </el-button>
                                    <template #dropdown>
                                        <el-dropdown-menu>
                                            <el-dropdown-item v-if="showDocuments" :disabled="!scope.row.initial_acceptance?.technical_application" @click="download(scope.row.initial_acceptance.technical_application)"><el-icon><Document /></el-icon>Technical application</el-dropdown-item>
                                            <el-dropdown-item v-if="showDocuments" :disabled="!scope.row.initial_acceptance?.preliminary_report" @click="download(scope.row.initial_acceptance.preliminary_report)"><el-icon><Document /></el-icon>Preliminary report</el-dropdown-item>
                                            <el-dropdown-item v-if="showDocuments" :disabled="!scope.row.initial_acceptance?.upgrade_plan" @click="download(scope.row.initial_acceptance.upgrade_plan)"><el-icon><Document /></el-icon>Upgrade plan</el-dropdown-item>
                                            <el-dropdown-item v-if="showDocuments" :disabled="!scope.row.initial_acceptance?.final_report" @click="download(scope.row.initial_acceptance.final_report)"><el-icon><Document /></el-icon>Final report</el-dropdown-item>
                                        </el-dropdown-menu>
                                    </template>
                                </el-dropdown>
                            </el-button-group>
                        </template>
                        <router-link :to="{name: 'QaHome', params: { facilityId: scope.row.id }}" custom v-slot="{navigate}" v-if="showQa">
                            <el-button type="primary" plain round size="small" class="flex-grow-1" style="flex-basis: 100%;" icon="Right" :disabled="!facilityEvaluated (scope.row)" @click="navigate">Quality Assurance</el-button>
                        </router-link>
                    </div>
                </template>
            </el-table-column>
        </el-table>
        <el-row justify="center" class="my-5">
            <el-pagination
                v-model:current-page="currentPage"
                v-model:page-size="settings.perPage"
                :total="totalCount"
                :page-sizes="settings.perPageOptions"
                layout="prev, pager, next, sizes, total"
                @size-change="pageSizeChanged"
                @update:current-page="refreshTable"
                background
                style="justify-content: end"
            />
        </el-row>
        <el-dialog v-if="canAddFacility && !waitingForReview && !evaluatedOnly" v-model="addNFDialogVisible" title="Add National Facility" width="800px">
            <div>
                <AddFacilityForm cancel @cancel="addNFDialogVisible = false" @save="addFacility" />
            </div>
        </el-dialog>
    </div>
</template>

<script>
import api from '@/lib/api'

import { ElMessageBox } from 'element-plus'

import AddFacilityForm from '@/components/facilities/AddFacilityForm.vue'
import EditFacilityForm from '@/components/facilities/EditFacility.vue'
// import FacilityAcceptanceReviewers from '../cars/initial_acceptance/FacilityAcceptanceReviewers.vue'

import { mapGetters } from 'vuex'

export default {
    name: 'FacilitiesTable',
    props: {
        waitingForReview: { type: Boolean, default: false },
        evaluatedOnly: { type: Boolean, default: false },
        showProfile: { type: Boolean, default: false },
        showAcceptance: { type: Boolean, default: false },
        showQa: { type: Boolean, default: false },
        showDocuments: { type: Boolean, default: false },
        showReviewers: { type: Boolean, default: false },
        showContact: { type: Boolean, default: true },
        showAdminActions: { type: Boolean, default: false },
    },
    components: { AddFacilityForm, EditFacilityForm },
    data: () => {
        return {
            displayedFacilities: [],
            pages: undefined,
            currentPage: 1,
            totalCount: undefined,
            facilitiesLoading: false,
            settings: {
                perPageOptions: [10, 20, 50, 100],
                perPage: 10,
                query: ''
            },
            refreshTimeout: null,
            canAddFacility: false,
            addNFDialogVisible: false,
        }
    },
    computed: {
        ...mapGetters('auth', ['isAdmin'])
    },
    methods: {
        pageSizeChanged () {
            this.refreshTable()
        },
        queryChanged () {
            this.facilitiesLoading = true

            if (this.refreshTimeout) {
                clearTimeout(this.refreshTimeout)
            }

            const refreshTable = this.refreshTable

            this.refreshTimeout = setTimeout(function() {
                refreshTable()
            }, 500)
        },
        refreshTable () {
            this.facilitiesLoading = true

            let options = {
                per_page: this.settings.perPage,
                page: this.currentPage,
                search: this.settings.query
            }

            if (this.waitingForReview) {
                options.waiting_ia_review = true
            }

            if (this.evaluatedOnly) {
                options.evaluated = true
            }

            api.facilities.getFacilities (options).then(async response => {
                this.currentPage = response.data.pagination.page
                this.pages = response.data.pagination.pages
                this.displayedFacilities = response.data.national_facilities
                this.totalCount = response.data.pagination.total

                this.displayedFacilities = await Promise.all(this.displayedFacilities.map(async facility => {
                    const ia = await api.facilities.getInitialAcceptance(facility.id)
                    return { ...facility, initial_acceptance: ia.data }
                }))

                this.facilitiesLoading = false
            }).catch(error => {
                this.displayedFacilities = []
                this.currentPage = 1
                this.pages = undefined
                this.totalCount = undefined

                const err = error?.response?.data?.message || error.message

                this.$notify({
                    title: 'Error',
                    message: `Could not load facilities: ${err}`,
                    type: 'error'
                })
            })
        },
        openAddNFDialog () {
            this.addNFDialogVisible = true
        },
        addFacility (facility) {
            api.facilities.addFacility (facility).then(() => {
                this.refreshTable()
                this.addNFDialogVisible = false

                this.$notify({
                    title: 'Success',
                    message: `Facility saved successfully.`,
                    type: 'success'
                })
            }).catch(error => {
                const err = error?.response?.data?.message || error.message

                this.$notify({
                    title: 'Error',
                    message: `Could not save facility: ${err}`,
                    type: 'error'
                })
            })
        },
        initialAcceptanceColor (facility) {
            const code = facility.initial_acceptance?.status?.code || ''

            switch (code) {
                case 'submitted':
                case 'preliminary_report_issued':
                case 'waiting_final_report':
                    return 'warning'
                case 'evaluated':
                    return 'success'
                default:
                    return 'info'
            }
        },
        initialAcceptanceText (facility) {
            return facility.initial_acceptance?.status?.name || 'N/A'
        },
        canSendInvite (facility) {
            const code = facility?.initial_acceptance?.status?.code
            return  code === 'registered' || code === 'invited'
        },
        sendInvite (facilityId) {
            api.facilities.invite (facilityId).then(() => {
                this.$notify({
                    title: 'Success',
                    message: `Facility invitation sent successfully.`,
                    type: 'success'
                })
            }).catch(error => {
                const err = error?.response?.data?.message || error.message

                this.$notify({
                    title: 'Error',
                    message: `Could not send facility invitation: ${err}`,
                    type: 'error'
                })
            })
        },
        confirmInvite (facilityId) {
            ElMessageBox.confirm(
                "Are you sure you want to send the invitation email to the facility owner?",
                'Please confirm',
                { confirmButtonText: 'Send', cancelButtonText: 'Cancel', type: 'warning' }
            ).then ( () => {
                this.sendInvite(facilityId)
            }).catch ( () => {} )
        },
        facilityEvaluated ( facility ) {
            return facility.initial_acceptance?.status?.code === 'evaluated'
        },
        download (file) {
            api.file.getFile (file.id).then(response => {
                let filename = response.headers['content-disposition'].split('filename=')[1].split(';')[0]

                if (["'", '"'].indexOf(filename[0]) != -1) {
                    filename = filename.slice(1,-1)
                }

                let link = document.createElement('a')
                let binaryData = []
                binaryData.push(response.data)
                link.href = window.URL.createObjectURL (new Blob(binaryData, {type: response.headers['content-type']}))
                link.download = filename

                link.click()
            }).catch(error => {
                let err = error?.response?.data?.message || error.message

                if (error?.response?.status === 404) {
                    err = 'File not found'
                }

                this.$notify({
                    title: 'Error',
                    message: `Could not download file: ${err}`,
                    type: 'error'
                })
            })
        },

        reloadSavedFacility (facility) {
            this.displayedFacilities = this.displayedFacilities.map(f => f.id === facility.id ? {...f, ...facility} : f)
        }
    },
    async mounted() {
        this.refreshTable()

        api.facilities.getFacilitiesCreate().then(() => {this.canAddFacility = true}).catch(() => {this.canAddFacility = false})
    }
}
</script>

<style>
.el-badge__content.facility-status-badge {
    position: relative;
    transform: translateY(-50%) translateX(0%);
    top: -0.5em;
}

td.facility-cell  div.cell {
    overflow-x: visible !important;
}
</style>